import { privateApi, API_CONFIG } from '../config/api';
import axios from 'axios';

class AuthService {
  // Login user
  async login(credentials) {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/login/`, {
        username: credentials.email,
        password: credentials.password,
        captcha: '', // Add empty captcha field
      });


      // Handle different response formats
      let accessToken = response.data.access_token || response.data.access || response.data.key;
      let refreshToken = response.data.refresh_token || response.data.refresh;
      let user = response.data.user;

      if (accessToken) {
        localStorage.setItem('access_token', accessToken);
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        }
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }
      }

      return response.data;
    } catch (error) {
      console.error('Login error details:', error.response?.data);
      // Handle the new error response format
      const errorData = error.response?.data;
      
      // Check for specific error structures
      if (errorData) {
        // Handle array-based message structure
        if (errorData.message && Array.isArray(errorData.message) && errorData.message[0]?.message) {
          throw errorData.message[0].message;
        }
        // Handle non_field_errors
        else if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          throw errorData.non_field_errors[0];
        }
        // Handle detail field
        else if (errorData.detail) {
          throw errorData.detail;
        }
        // Handle direct message
        else if (errorData.message && typeof errorData.message === 'string') {
          throw errorData.message;
        }
        // Handle string error data
        else if (typeof errorData === 'string') {
          throw errorData;
        }
        // Handle field-specific errors (like email, password, etc.)
        else {
          const fieldErrors = Object.keys(errorData).filter(key => Array.isArray(errorData[key]));
          if (fieldErrors.length > 0) {
            const firstFieldError = errorData[fieldErrors[0]];
            if (firstFieldError[0]?.message) {
              throw firstFieldError[0].message;
            } else if (typeof firstFieldError[0] === 'string') {
              throw firstFieldError[0];
            }
          }
        }
      }
      
      throw error.message || 'Login failed';
    }
  }

  // Register user
  async register(userData) {
    try {
      const registrationData = {
        username: userData.email,
        email: userData.email,
        password1: userData.password,
        password2: userData.confirmPassword,
        first_name: userData.firstName,
        last_name: userData.lastName,
        country: userData.country,
        captchaResponse: '',  // Required field but can be empty
        lang: 'en',
      };

      
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/registration/`, registrationData);

      return response.data;
    } catch (error) {
      if (error.response?.status === 500) {
        throw new Error('Email Already Exist. Please Login');
      }
      console.error('Registration error details:', error.response?.data);
      // Handle the new error response format
      const errorData = error.response?.data;
      
      // Check for specific error structures
      if (errorData) {
        // Handle field-specific errors (like username, email, password1, password2, etc.)
        const fieldErrors = Object.keys(errorData).filter(key => Array.isArray(errorData[key]));
        if (fieldErrors.length > 0) {
          const firstFieldError = errorData[fieldErrors[0]];
          if (firstFieldError[0]?.message) {
            throw firstFieldError[0].message;
          } else if (typeof firstFieldError[0] === 'string') {
            throw firstFieldError[0];
          }
        }
        // Handle array-based message structure
        else if (errorData.message && Array.isArray(errorData.message) && errorData.message[0]?.message) {
          throw errorData.message[0].message;
        }
        // Handle non_field_errors
        else if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          throw errorData.non_field_errors[0];
        }
        // Handle detail field
        else if (errorData.detail) {
          throw errorData.detail;
        }
        // Handle direct message
        else if (errorData.message && typeof errorData.message === 'string') {
          throw errorData.message;
        }
        // Handle string error data
        else if (typeof errorData === 'string') {
          throw errorData;
        }
      }
      
      throw error.message || 'Registration failed';
    }
  }

  // Logout user
  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('api_key');
    localStorage.removeItem('secret_key');
  }

  // Get current user
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }

  // Get user profile
  async getProfile() {
    try {
      const response = await privateApi.get('/v1/profile/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Update user profile
  async updateProfile(profileData) {
    try {
      const response = await privateApi.post('/v1/profile/', profileData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Change password
  async changePassword(passwordData) {
    try {
      const response = await privateApi.post('/v1/auth/password/change/', {
        old_password: passwordData.oldPassword,
        new_password1: passwordData.newPassword,
        new_password2: passwordData.confirmNewPassword,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Reset password
  async resetPassword(email) {
    try {
      // Following the API structure: googlecode, email, lang, captcha (static values)
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/password/reset/`, {
        googlecode: "string", // Static value as per API requirement
        email: email, // Dynamic email from user input
        lang: "en", // Static language value
        captcha: "string" // Static captcha value as per API requirement
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Reset password confirmation
  async resetPasswordConfirm(resetData) {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/password/reset/confirm/`, {
        uid: resetData.uid,
        token: resetData.token,
        new_password1: resetData.new_password1,
        new_password2: resetData.new_password2,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Verify email
  async verifyEmail(key) {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/registration/verify-email/`, {
        key,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Verify OTP
  async verifyOTP(email, otp) {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/verify-otp/`, {
        email,
        otp,
      });
      return response.data;
    } catch (error) {
      // Handle the new error response format
      const errorData = error.response?.data;
      if (errorData && errorData.message) {
        throw errorData.message;
      } else if (errorData && typeof errorData === 'string') {
        throw errorData;
      } else {
        throw error.message || 'OTP verification failed';
      }
    }
  }

  // Resend OTP
  async resendOTP(email) {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/resend-otp/`, {
        email,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Enable 2FA
  async enable2FA() {
    try {
      const response = await privateApi.post('/v1/set2fa/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Disable 2FA
  async disable2FA(otpCode) {
    try {
      const response = await privateApi.post('/v1/remove2fa/', {
        otp_code: otpCode,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Generate API key
  async generateApiKey() {
    try {
      const response = await privateApi.post('/v1/regenerate-api-key/');
      if (response.data.api_key && response.data.secret_key) {
        localStorage.setItem('api_key', response.data.api_key);
        localStorage.setItem('secret_key', response.data.secret_key);
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get countries list
  async getCountries() {
    try {
      const response = await privateApi.get('/v1/countries/');
      // Convert the object format {code: name} to array format [{code, name}]
      const countriesData = response.data;
      if (typeof countriesData === 'object' && countriesData !== null) {
        return Object.entries(countriesData).map(([code, name]) => ({
          code,
          name
        }));
      }
      return [];
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
}

export default new AuthService(); 